import Service from '@ember/service';

/**
 * Sequence Tracker Service
 *
 * Tracks WebSocket message sequence numbers to detect gaps (missed messages).
 * When a gap is detected, triggers a callback to refresh data from REST API.
 *
 * Suppression is based on "refetch in flight" rather than wall-clock cooldown,
 * so subsequent gaps that arrive while the refetch runs aren't dropped — they
 * re-trigger once the refetch completes. resetSequenceTracking() (called by
 * the route after a successful refetch) is what clears the in-flight flag.
 */
export default class SequenceTrackerService extends Service {
  // Map of eventId -> { lastSequence: number, refetchInFlight: boolean }
  _sequenceTracking = new Map();

  // Callback for triggering refresh when sequence gap detected
  _onSequenceGap = null;

  /**
   * Set callback for sequence gap detection
   * Called when a gap is detected, allowing the route to trigger a refresh
   */
  setSequenceGapCallback(callback) {
    this._onSequenceGap = callback;
  }

  /**
   * Reset sequence tracking for an event. Called by the route after a REST
   * refetch completes so that a new gap can re-trigger a refresh.
   */
  resetSequenceTracking(eventId) {
    this._sequenceTracking.delete(eventId);
    console.log(`[SequenceTracker] Reset sequence tracking for event ${eventId}`);
  }

  /**
   * Check for sequence gaps and trigger refresh if needed
   * Returns true if a gap was detected
   */
  checkSequenceGap(eventId, sequence) {
    if (!sequence && sequence !== 0) return false;

    const tracking = this._sequenceTracking.get(eventId);

    if (!tracking) {
      // First message - initialize tracking
      this._sequenceTracking.set(eventId, {
        lastSequence: sequence,
        refetchInFlight: false
      });
      return false;
    }

    const expectedSequence = tracking.lastSequence + 1;

    if (sequence > expectedSequence) {
      const gapSize = sequence - expectedSequence;
      console.warn(`[SequenceTracker] Sequence gap detected for event ${eventId}: expected ${expectedSequence}, got ${sequence} (gap of ${gapSize})`);

      // Always advance lastSequence so we don't re-flag the same gap for every
      // subsequent message, but only fire the callback when no refetch is
      // already in flight. The route clears refetchInFlight by calling
      // resetSequenceTracking when its REST load finishes.
      tracking.lastSequence = sequence;

      if (!tracking.refetchInFlight) {
        tracking.refetchInFlight = true;
        if (this._onSequenceGap) {
          console.log(`[SequenceTracker] Triggering refresh due to sequence gap`);
          this._onSequenceGap(eventId);
        }
        return true;
      }

      console.log(`[SequenceTracker] Gap detected but refetch already in flight - will re-check when it completes`);
      return false;
    }

    // Update tracking (handle out-of-order but keep highest)
    if (sequence > tracking.lastSequence) {
      tracking.lastSequence = sequence;
    }

    return false;
  }
}
